// <copyright>Copyright (c) Microsoft Corporation. All rights reserved.</copyright>

import { type RpcMessage } from "Rpc/RpcMessage";
import { type RpcMessageTypes } from "Rpc/RpcMessageTypes";
import { type IRpcMessageConverter } from "Rpc/RpcProtocol/IRpcMessageConverter";
import { type RpcRequestMessage } from "Rpc/RpcRequestMessage";
import { type RpcResponseMessage } from "Rpc/RpcResponseMessage";
import * as ObjectUtils from "Utils/ObjectUtils";

interface AzureMessageFrame {
    id: string;
    kind: string;
    signature: string;
    data: RpcMessage;
}

export class AzureMessageConverter implements IRpcMessageConverter<AzureMessageFrame> {
    public readonly signature: string;

    private static readonly emptyValue: string = "";
    private static readonly frameSignature: string = "FxFrameControl";

    constructor(signature: string) {
        this.signature = signature;
    }

    public deserialize(event: MessageEvent<AzureMessageFrame>): RpcRequestMessage | RpcResponseMessage | undefined {
        // Azure portal will automatically strip the first layer so
        // if there is a data property, return data else return the message as is (which may be undefined).
        const message: AzureMessageFrame | RpcMessage = event.data;

        return ObjectUtils.isRecord(message) ? ObjectUtils.getPropertyOrDefault(message, "data", message) : message;
    }

    public serialize(message: RpcMessageTypes): AzureMessageFrame {
        message.signature = this.signature;

        return {
            id: message.id,
            kind: ObjectUtils.getPropertyOrDefault<string>(message, "method", AzureMessageConverter.emptyValue),
            signature: AzureMessageConverter.frameSignature,
            data: message,
        };
    }
}
